import {
  init,
  makeBrowserOfflineTransport,
  makeFetchTransport,
  reactRouterV5BrowserTracingIntegration,
  replayIntegration,
} from '@sentry/react';
import { assert, isBotUserAgent, isE2ESession, isFeatureEnabled, isProduction } from 'common/utils';
import { reduxHistory } from 'reduxHistory';

const REPLAY_WHITE_LIST_SEVERITY = ['error', 'warning', 'fatal'];

export const initSentry = () => {
  if (isE2ESession() || isBotUserAgent()) {
    return;
  }

  assert(window._env_.VITE_APP_API_BASE_URL);
  init({
    dsn: window._env_.VITE_APP_SENTRY_DSN,
    integrations: [
      reactRouterV5BrowserTracingIntegration({ history: reduxHistory }),
      replayIntegration({
        maskAllText: isProduction(),
        maskAllInputs: isProduction(),
        blockAllMedia: isProduction(),
        /**
         * https://docs.sentry.io/platforms/javascript/session-replay/understanding-sessions/#ignore-certain-errors-for-error-sampling
         * don't record replays for simple logs
         */
        beforeErrorSampling: (event) => (event.level ? REPLAY_WHITE_LIST_SEVERITY.includes(event.level) : true),
      }),
    ],
    release: window._env_.VITE_APP_TAG,
    environment: window._env_.VITE_APP_ENVIRONMENT,
    enabled: isFeatureEnabled('SentryEnabled'),
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    normalizeDepth: 9,
    transport: makeBrowserOfflineTransport(makeFetchTransport),
    beforeSend: (event, hint) => {
      // Filter out CefSharp email scanner errors
      // These are generated by email security scanners (e.g., Outlook Safe Links)
      // that pre-check URLs with an embedded browser that has known bugs
      // See: https://github.com/DataDog/browser-sdk/issues/2715
      const cefSharpErrorPattern = /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/;

      // Check exception message
      if (event.exception?.values?.[0]?.value) {
        if (cefSharpErrorPattern.test(event.exception.values[0].value)) {
          return null;
        }
      }

      // Check error message from hint
      if (hint?.originalException) {
        const error = hint.originalException;
        if (typeof error === 'string' && cefSharpErrorPattern.test(error)) {
          return null;
        }
        if (error instanceof Error && cefSharpErrorPattern.test(error.message)) {
          return null;
        }
        if (typeof error === 'object' && error !== null && 'message' in error) {
          const message = (error as { message: unknown }).message;
          if (typeof message === 'string' && cefSharpErrorPattern.test(message)) {
            return null;
          }
        }
      }

      return event;
    },
  });
};
